import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Softphone } from '../components/Softphone';
import { IncidentForm } from '../components/IncidentForm';
import { Banner, Card, Empty, IncidentBadge, PriorityPill } from '../components/ui';
import { useSoftphone } from '../telephony/SoftphoneContext';
import { useFlash, useNow, useResource } from '../state/hooks';
import { api } from '../lib/api';
import { clock, dateTime, elapsed, phone } from '../lib/format';
import type { Category, Incident, Priority } from '../lib/types';
import { useAuth } from '../state/AuthContext';

interface CallerLookup {
  caller: {
    id: string;
    full_name: string | null;
    address: string | null;
    latitude: number | null;
    longitude: number | null;
    notes: string | null;
  } | null;
  priorIncidents: {
    id: string;
    incident_no: string;
    status: string;
    location_address: string | null;
    created_at: string;
  }[];
}

/**
 * The call taker's console: handset on the left, intake on the right.
 *
 * The two are coupled in exactly one direction — answering a call pre-fills the
 * form from the caller database and attaches the call to whatever incident the
 * form produces. Nothing about the form depends on a call existing, because
 * walk-ins and radio reports come in the same way.
 */
export function CallTakerConsole() {
  const { user } = useAuth();
  const softphone = useSoftphone();
  const now = useNow(5000);
  const [flash, showFlash] = useFlash();

  const taxonomy = useResource<{ categories: Category[]; priorities: Priority[] }>('/admin/taxonomy');
  const mine = useResource<{ incidents: Incident[] }>(
    user ? `/incidents?createdBy=${user.id}&limit=15` : null,
  );

  const call = softphone.activeCall;
  const callerNumber = call?.direction === 'inbound' ? call.ani : call?.dnis;
  const [lookup, setLookup] = useState<CallerLookup | null>(null);

  // The ANI lookup runs once per caller, not once per render — an operator
  // typing into the form must never trigger a refetch that resets it.
  useEffect(() => {
    if (!callerNumber) {
      setLookup(null);
      return;
    }
    let cancelled = false;
    void api
      .get<CallerLookup>(`/calls/lookup/${encodeURIComponent(callerNumber)}`)
      .then((result) => {
        if (!cancelled) setLookup(result);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [callerNumber]);

  const prefill = useMemo(() => {
    if (!call) return undefined;
    return {
      callerName: call.known_caller_name ?? call.caller_id_name ?? '',
      callerPhone: callerNumber ?? '',
      locationAddress: lookup?.caller?.address ?? call.known_caller_address ?? '',
      latitude: lookup?.caller?.latitude ?? call.known_caller_latitude ?? null,
      longitude: lookup?.caller?.longitude ?? call.known_caller_longitude ?? null,
    };
  }, [call, callerNumber, lookup]);

  return (
    <div className="columns side">
      <div className="stack">
        <Softphone />

        {lookup?.caller && (
          <Card title="Caller on file">
            <div className="stack tight">
              <div><strong>{lookup.caller.full_name ?? 'Unnamed'}</strong></div>
              {lookup.caller.address && <div className="muted">{lookup.caller.address}</div>}
              {lookup.caller.notes && <Banner kind="warn">{lookup.caller.notes}</Banner>}
            </div>
          </Card>
        )}

        {lookup && lookup.priorIncidents.length > 0 && (
          <Card title={`Previous calls from this number (${lookup.priorIncidents.length})`} flush>
            <ul className="list scroll-sm">
              {lookup.priorIncidents.map((prior) => (
                <li key={prior.id}>
                  <Link to={`/incidents/${prior.id}`} className="item">
                    <span className="grow">
                      <span className="item-title mono">{prior.incident_no}</span>
                      <span className="item-sub">
                        {prior.location_address ?? 'No address'} · {dateTime(prior.created_at)}
                      </span>
                    </span>
                    <span className="badge neutral">{prior.status.replace('_', ' ')}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      <div className="stack">
        {flash && <Banner kind={flash.kind}>{flash.message}</Banner>}

        <Card
          title={call ? `New incident — call from ${phone(callerNumber)}` : 'New incident'}
          actions={
            call ? (
              <span className="badge accent">Will be attached to this call</span>
            ) : (
              <span className="faint">No call in progress — logging a walk-in or radio report</span>
            )
          }
        >
          {taxonomy.data ? (
            <IncidentForm
              categories={taxonomy.data.categories}
              priorities={taxonomy.data.priorities}
              prefill={prefill}
              callId={call?.id ?? null}
              onSaved={(incident, sentToDispatch) => {
                showFlash(
                  'ok',
                  sentToDispatch
                    ? `${incident.incident_no} sent to dispatch.`
                    : `${incident.incident_no} saved as a draft.`,
                );
                mine.reload();
                // Attaching keeps the call record pointing at the incident even
                // if the operator raises a second one from the same call.
                if (call) void softphone.attachToIncident(incident.id).catch(() => undefined);
              }}
            />
          ) : (
            <Empty>Loading incident types…</Empty>
          )}
        </Card>

        <Card title="Raised by you today" flush>
          {!mine.data ? (
            <Empty>Loading…</Empty>
          ) : mine.data.incidents.length === 0 ? (
            <Empty>Nothing raised yet this shift.</Empty>
          ) : (
            <ul className="list scroll-md">
              {mine.data.incidents.map((incident) => (
                <li key={incident.id}>
                  <Link to={`/incidents/${incident.id}`} className="item">
                    <PriorityPill
                      code={incident.priority_code}
                      color={incident.priority_color}
                      level={incident.priority_level}
                    />
                    <span className="grow">
                      <span className="item-title mono">{incident.incident_no}</span>
                      <span className="item-sub truncate">
                        {incident.category_name ?? 'Unclassified'} · {incident.location_address ?? 'No address'}
                      </span>
                    </span>
                    <span className="faint">{clock(incident.created_at)}</span>
                    <IncidentBadge status={incident.status} />
                    <span className="faint">{elapsed(incident.created_at, now)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
